import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { getAdminUsers, setUserRole, inviteUser, saveUserTeam, removeUserAccess, resendInvite,
         getReaders, setReaderGroup, setReaderGroupEmail, saveUserMonthlyEmail,
         setReaderEmail, setReaderAccount, getReaderCleanup, resolveReaderCleanup } from "../lib/api";
import { Combobox } from "../components/SharedComponents";

/* Users: Super Admins manage who has which portal role.

   Roles are Entra ID groups; the backend changes group membership with its own
   identity, so nobody here needs Azure access. The list merges everyone who
   holds a role with every technologist in the USMON techs view, so a tech who
   has never signed in still shows up and can be invited. Team is a portal-only
   field for techs, picked from the regions in the case data.

   Rows are read-only until someone clicks Edit, one row at a time, so a stray
   click can't change anyone's access. */

const ROLE_STYLE = {
  SuperAdmin: { label: "Super Admin", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  Admin:      { label: "Admin",       color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  Member:     { label: "Member",      color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
  Tech:       { label: "Tech",        color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
};

function loginState(u) {
  if (!u.entraId) return { label: "No account", color: "#94a3b8" };
  if (u.userType === "Guest" && u.inviteState === "PendingAcceptance") return { label: "Invite pending", color: "#d97706" };
  if (u.userType === "Guest") return { label: "Guest", color: "#0f766e" };
  return { label: "Member", color: "#0f766e" };
}

/* Add or edit one group: its name and the addresses its IP reports go to.
   Emails are optional - a group without one still works everywhere else. */
function GroupDialog({ group, emails, existing, busy, onCancel, onSave }) {
  const isNew = !group;
  const [name, setName]   = useState(group || "");
  // One address per line: simpler to paste into than a row of inputs.
  const [text, setText]   = useState((emails || []).join("\n"));
  const [error, setError] = useState(null);

  const submit = () => {
    const n = name.trim();
    if (!n) return setError("Give the group a name.");
    if (isNew && existing.includes(n)) return setError(`There's already a group called ${n}.`);
    const list = text.split(/[\n,;]+/).map(a => a.trim()).filter(Boolean);
    const bad = list.find(a => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a));
    if (bad) return setError(`That doesn't look like an email address: ${bad}`);
    setError(null);
    onSave(n, list);
  };

  return (
    <div onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} className="card"
        style={{ width: "100%", maxWidth: 440, overflow: "visible" }}>
        <div className="card-header">
          <div>
            <div className="card-title">{isNew ? "Add a group" : `Edit ${group}`}</div>
            <div className="card-subtitle">Readers are put into groups on this page. Email is optional.</div>
          </div>
        </div>
        <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginBottom: 4 }}>Group name</div>
            <input value={name} onChange={e => setName(e.target.value)} disabled={!isNew} autoFocus={isNew}
              placeholder="e.g. Southwest Readers"
              style={{ width: "100%", fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)",
                boxSizing: "border-box", background: isNew ? "var(--white)" : "var(--gray-50, #f9fafb)" }}/>
          </div>
          <div>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginBottom: 4 }}>
              Email addresses (optional)
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4} autoFocus={!isNew}
              placeholder={"one@example.com\ntwo@example.com"}
              style={{ width: "100%", fontSize: 12.5, fontFamily: "var(--font-mono)", padding: "7px 10px", borderRadius: 6,
                border: "1px solid var(--border)", boxSizing: "border-box", resize: "vertical" }}/>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
              One per line. This is where the group's IP reports will be sent.
            </div>
          </div>
          {error && (
            <div style={{ fontSize: 12.5, color: "var(--error)", background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6, padding: "7px 11px" }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
              {busy ? "Saving…" : isNew ? "Add group" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* Readers whose portal access was removed. Removing access takes them out of
   the IP group but leaves the guest account, because deleting an account is
   irreversible and may not be ours to decide. This list is what stops those
   accounts being forgotten: it checks Entra on load rather than trusting what
   was written down, so a row clears itself once the account is really gone. */
function CleanupPanel() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    getReaderCleanup().then(r => setRows(r.removals || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function resolve(r) {
    setBusy(r.readerId);
    try { await resolveReaderCleanup(r.readerId); load(); } finally { setBusy(""); }
  }

  if (!rows || rows.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 14, borderLeft: "3px solid #d97706" }}>
      <div style={{ padding: "12px 16px" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
          Accounts to clean up ({rows.length})
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 10 }}>
          Portal access was removed, but the guest account still exists in Entra. Delete it there,
          then mark it handled.
        </div>
        {rows.map(r => (
          <div key={r.readerId} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "7px 0",
            borderTop: "1px solid var(--border)",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5 }}>{r.name || r.readerId}</div>
              <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
                {r.email} · removed {r.removedAt ? new Date(r.removedAt).toLocaleDateString() : "—"}
                {r.removedBy ? ` by ${r.removedBy}` : ""}
              </div>
            </div>
            <span style={{
              fontSize: 10, fontFamily: "var(--font-mono)", padding: "2px 7px", borderRadius: 4,
              background: r.accountExists === false ? "#d1fae5" : "#fef3c7",
              color: r.accountExists === false ? "#059669" : "#92400e",
              border: `1px solid ${r.accountExists === false ? "#a7f3d0" : "#fde68a"}`,
            }}>
              {r.accountExists === null ? "Entra unreachable"
                : r.accountExists ? "Account still active" : "Account gone"}
            </span>
            <button className="btn btn-secondary btn-sm" disabled={busy === r.readerId}
              onClick={() => resolve(r)}>Mark handled</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Readers: each IP (reader) can be put in one group, which IP Reporting can
   filter by. Readers mostly don't sign in, so this list comes from the case
   data (anyone who read a case in the last year), not from accounts. The group
   list is kept by the backend: a group typed here is added to it. */
function ReadersPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ]             = useState("");
  const [groupF, setGroupF]   = useState("all");

  const load = () => {
    setLoading(true); setError(null);
    return getReaders()
      .then(setData)
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const groups = data?.groups || [];
  const groupEmails = data?.groupEmails || {};
  const [groupDialog, setGroupDialog] = useState(null);   // "" for a new group, or the group to edit

  const saveGroupEmails = async (group, emails) => {
    setBusy("group:" + group);
    try { await setReaderGroupEmail(group, emails); await load(); setGroupDialog(null); }
    catch (e) { alert(e?.message || String(e)); }
    setBusy(null);
  };
  const [emailDraft, setEmailDraft] = useState({});
  const [rowErr, setRowErr] = useState({});

  async function saveEmail(r, email) {
    setBusy(r.readerId);
    setRowErr(e => ({ ...e, [r.readerId]: "" }));
    try {
      await setReaderEmail(r.readerId, r.name, email.trim());
      setEmailDraft(d => { const n = { ...d }; delete n[r.readerId]; return n; });
      await load();
    } catch (e) {
      setRowErr(er => ({ ...er, [r.readerId]: e.message || "Could not save that email" }));
    } finally { setBusy(""); }
  }

  async function toggleAccount(r) {
    const on = !r.account;
    if (on && !window.confirm(
      `Give ${r.name || r.readerId} a portal account?\n\n` +
      `Microsoft will email an invitation to ${r.email}. They will see only their own cases ` +
      `and their own IP report.`
    )) return;
    if (!on && !window.confirm(
      `Remove portal access for ${r.name || r.readerId}?\n\n` +
      `They come out of the IP group and lose access immediately. Their guest account is kept, ` +
      `and appears in the cleanup list so it is not forgotten.`
    )) return;
    setBusy(r.readerId);
    setRowErr(e => ({ ...e, [r.readerId]: "" }));
    try { await setReaderAccount(r.readerId, r.name, on); await load(); }
    catch (e) { setRowErr(er => ({ ...er, [r.readerId]: e.message || "That didn't work" })); }
    finally { setBusy(""); }
  }

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.readers || []).filter(r => {
      if (needle && !`${r.name} ${r.readerId} ${r.group}`.toLowerCase().includes(needle)) return false;
      if (groupF === "none") return !r.group;
      if (groupF !== "all") return r.group === groupF;
      return true;
    });
  }, [data, q, groupF]);

  const save = async (r, group) => {
    if ((group || "") === (r.group || "")) return;
    setBusy(r.readerId);
    try { await setReaderGroup(r.readerId, r.name, group); await load(); setEditing(null); }
    catch (e) { alert(e?.message || String(e)); }
    setBusy(null);
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, reader ID or group…"
          style={{ flex: "1 1 240px", maxWidth: 340, fontSize: 12.5, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)" }}/>
        <select value={groupF} onChange={e => setGroupF(e.target.value)}
          style={{ fontSize: 12, fontFamily: "var(--font-mono)", padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--white)" }}>
          <option value="all">All groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
          <option value="none">No group</option>
        </select>
        <button className="btn btn-secondary btn-sm" disabled={!!busy}
          onClick={() => setGroupDialog(groupF !== "all" && groupF !== "none" ? groupF : "")}>
          {groupF !== "all" && groupF !== "none" ? "Edit group" : "Add group"}
        </button>
        <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
          {rows.length} {rows.length === 1 ? "reader" : "readers"}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading} style={{ marginLeft: "auto" }}>Refresh</button>
      </div>

      {groupDialog !== null && (
        <GroupDialog group={groupDialog} emails={groupEmails[groupDialog] || []} existing={groups}
          busy={busy === "group:" + groupDialog}
          onCancel={() => setGroupDialog(null)} onSave={saveGroupEmails}/>
      )}

      {loading && !data ? (
        <div className="card" style={{ padding: 20 }}><div className="skeleton" style={{ height: 200, borderRadius: 8 }}/></div>
      ) : error ? (
        <div className="card" style={{ padding: "28px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Couldn't load readers</div>
          <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", marginBottom: 12 }}>{error}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>Try again</button>
        </div>
      ) : (
        // overflow visible so the group dropdown isn't clipped by the card.
        <div className="card">
          {/* Same as the People table: .card clips overflow, so the table
              scrolls sideways in its own wrapper rather than spilling onto
              the page. The wrapper opens up while a row is being edited,
              because the group Combobox menu is positioned absolutely and a
              scrolling ancestor would cut it off. */}
          <div style={{ overflowX: editing ? "visible" : "auto" }}>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Reader ID</th>
                <th>Group</th>
                <th>Account</th>
                <th style={{ textAlign: "right" }}>Cases</th>
                <th>Last case</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const rowBusy = busy === r.readerId;
                const isEditing = editing === r.readerId;
                return (
                  <tr key={r.readerId} style={{ opacity: rowBusy ? 0.5 : 1 }}>
                    <td style={{ fontSize: 12.5, fontWeight: 500 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{r.name || "—"}</span>
                        {r.dormant && (
                          <span title="No cases in the last year. Listed because the portal holds a record."
                            style={{ fontSize: 9.5, fontFamily: "var(--font-mono)", color: "#d97706",
                                     background: "#fffbeb", border: "1px solid #fde68a",
                                     borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>
                            dormant
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <input
                          value={emailDraft[r.readerId] ?? r.email ?? ""}
                          onChange={e => setEmailDraft(d => ({ ...d, [r.readerId]: e.target.value }))}
                          onBlur={e => { if ((e.target.value || "") !== (r.email || "")) saveEmail(r, e.target.value); }}
                          placeholder="physician@example.com"
                          style={{ width: "100%", marginTop: 4, fontSize: 11.5, padding: "4px 7px",
                                   borderRadius: 5, border: "1px solid var(--border)",
                                   background: "var(--white)", color: "var(--text-primary)" }} />
                      ) : r.email ? (
                        <div style={{ fontSize: 10.5, fontFamily: "var(--font-mono)",
                                      color: "var(--text-tertiary)", fontWeight: 400,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.email}
                        </div>
                      ) : null}
                      {rowErr[r.readerId] && (
                        <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 3, fontWeight: 400 }}>
                          {rowErr[r.readerId]}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{r.readerId}</td>
                    <td style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {!isEditing ? (
                        r.group
                          ? <>
                              {r.group}
                              {(groupEmails[r.group] || []).length > 0 && (
                                <div style={{ fontSize: 10.5, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
                                  {groupEmails[r.group].join(", ")}
                                </div>
                              )}
                            </>
                          : <span style={{ color: "var(--text-tertiary)" }}>—</span>
                      ) : (
                        <Combobox value={r.group || ""} onChange={() => {}} onSelect={g => save(r, g)}
                          options={groups} placeholder="Pick or type a new group…"/>
                      )}
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {r.account ? (
                        <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", padding: "2px 7px",
                                       borderRadius: 4, background: "#d1fae5", color: "#059669",
                                       border: "1px solid #a7f3d0" }}>Has account</span>
                      ) : (
                        <span style={{ color: "var(--text-tertiary)", fontSize: 11.5 }}>No account</span>
                      )}
                      {isEditing && (
                        <div style={{ marginTop: 4 }}>
                          <button className="btn btn-secondary btn-sm" disabled={rowBusy || !r.email}
                            title={r.email ? "" : "Add an email first"}
                            onClick={() => toggleAccount(r)} style={{ fontSize: 10.5 }}>
                            {r.account ? "Remove access" : "Give account"}
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontSize: 12 }}>{r.caseCount ?? "—"}</td>
                    <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{r.lastCase || "—"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {isEditing && r.group && (
                        <button className="btn btn-secondary btn-sm" disabled={rowBusy} onClick={() => save(r, "")}
                          style={{ marginRight: 6 }}>Clear group</button>
                      )}
                      {isEditing ? (
                        <button className="btn btn-primary btn-sm" disabled={rowBusy} onClick={() => setEditing(null)}>Done</button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => setEditing(r.readerId)}>Edit</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--text-tertiary)" }}>No one matches.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminUsers() {
  const [tab, setTab] = useState("people");
  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Users</div>
          {/* One subtitle for the page, not one per tab: the heading should not
              change under someone switching tabs. */}
          <div className="page-subtitle">Portal roles and sign-in invitations</div>
        </div>
      </div>
      <div className="page-body">
        <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "1px solid var(--border)" }}>
          {[["people", "People"], ["readers", "Readers"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ fontSize: 12.5, fontWeight: tab === k ? 600 : 500, padding: "7px 14px", background: "none", cursor: "pointer",
                border: "none", borderBottom: `2px solid ${tab === k ? "var(--accent, #1d4ed8)" : "transparent"}`,
                color: tab === k ? "var(--text-primary)" : "var(--text-tertiary)", marginBottom: -1 }}>
              {label}
            </button>
          ))}
        </div>
        {tab === "people" ? <PeoplePanel/> : <><CleanupPanel/><ReadersPanel/></>}
      </div>
    </>
  );
}

function PeoplePanel() {
  const { accounts } = useMsal();
  const me = (accounts?.[0]?.username || "").toLowerCase();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);    // row key being changed
  const [editing, setEditing] = useState(null);    // row key open for editing
  // Roles picked for someone who has no account yet. Nothing is sent until
  // Invite: the account (and so the group membership) doesn't exist before then.
  const [inviteRoles, setInviteRoles] = useState([]);

  const startEdit = (u) => {
    setEditing(u.key);
    setInviteRoles(u.techWebId ? ["Tech"] : []);
  };
  const toggleInviteRole = (r) =>
    setInviteRoles(p => p.includes(r) ? p.filter(x => x !== r) : [...p, r]);
  const [q, setQ]             = useState("");
  const [roleF, setRoleF]     = useState("all");

  const load = () => {
    setLoading(true); setError(null);
    return getAdminUsers()
      .then(setData)
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const roles = data?.roles || [];
  const [emailDraft, setEmailDraft] = useState({});
  const [rowErr, setRowErr] = useState({});

  async function saveEmail(r, email) {
    setBusy(r.readerId);
    setRowErr(e => ({ ...e, [r.readerId]: "" }));
    try {
      await setReaderEmail(r.readerId, r.name, email.trim());
      setEmailDraft(d => { const n = { ...d }; delete n[r.readerId]; return n; });
      await load();
    } catch (e) {
      setRowErr(er => ({ ...er, [r.readerId]: e.message || "Could not save that email" }));
    } finally { setBusy(""); }
  }

  async function toggleAccount(r) {
    const on = !r.account;
    if (on && !window.confirm(
      `Give ${r.name || r.readerId} a portal account?\n\n` +
      `Microsoft will email an invitation to ${r.email}. They will see only their own cases ` +
      `and their own IP report.`
    )) return;
    if (!on && !window.confirm(
      `Remove portal access for ${r.name || r.readerId}?\n\n` +
      `They come out of the IP group and lose access immediately. Their guest account is kept, ` +
      `and appears in the cleanup list so it is not forgotten.`
    )) return;
    setBusy(r.readerId);
    setRowErr(e => ({ ...e, [r.readerId]: "" }));
    try { await setReaderAccount(r.readerId, r.name, on); await load(); }
    catch (e) { setRowErr(er => ({ ...er, [r.readerId]: e.message || "That didn't work" })); }
    finally { setBusy(""); }
  }

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.users || []).filter(u => {
      if (needle && !`${u.name} ${u.email} ${u.team} ${u.techWebId || ""}`.toLowerCase().includes(needle)) return false;
      if (roleF === "none") return u.roles.length === 0;
      if (roleF === "techs-no-login") return !!u.techWebId && !u.entraId;
      if (roleF !== "all") return u.roles.includes(roleF);
      return true;
    });
  }, [data, q, roleF]);

  const act = async (u, fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(u.key);
    try { await fn(); await load(); }
    catch (e) { alert(e?.message || String(e)); }
    setBusy(null);
  };

  const toggleRole = (u, role) => {
    const has = u.roles.includes(role);
    const label = ROLE_STYLE[role]?.label || role;
    act(u, () => setUserRole(u.entraId, role, !has),
        has ? `Remove ${label} from ${u.name || u.email}?` : `Give ${u.name || u.email} the ${label} role?`);
  };

  const invite = (u) => {
    const give = roles.filter(r => inviteRoles.includes(r));
    const names = give.map(r => ROLE_STYLE[r]?.label || r).join(", ");
    act(u, async () => { await inviteUser(u.email, u.name, give); setEditing(null); },
        `Send ${u.name || u.email} a Microsoft sign-in invitation at ${u.email}` +
        (give.length ? ` and give them: ${names}?` : " with no role? They won't be able to open the portal until you add one."));
  };

  const setTeam = (u, team) => act(u, () => saveUserTeam(u.email, team));

  const setMonthlyEmail = (u, on) => act(u, () => saveUserMonthlyEmail(u.email, on));

  const resend = (u) =>
    act(u, async () => { await resendInvite(u.email, u.name); alert(`Invitation re-sent to ${u.email}.`); },
        `Send the invitation email to ${u.name || u.email} again at ${u.email}?`);

  const removeAccess = (u) => {
    const names = roles.filter(r => u.roles.includes(r)).map(r => ROLE_STYLE[r]?.label || r).join(", ");
    act(u, async () => { await removeUserAccess(u.entraId); setEditing(null); },
        `Remove all portal access for ${u.name || u.email}?

` +
        `This takes away: ${names}. They won't be able to sign in to the portal. ` +
        `Their account isn't deleted, so you can give them a role again later.`);
  };

  return (
    <>
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, team or USMON ID…"
            style={{ flex: "1 1 240px", maxWidth: 340, fontSize: 12.5, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)" }}/>
          <select value={roleF} onChange={e => setRoleF(e.target.value)}
            style={{ fontSize: 12, fontFamily: "var(--font-mono)", padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--white)" }}>
            <option value="all">Everyone</option>
            {roles.map(r => <option key={r} value={r}>{ROLE_STYLE[r]?.label || r}</option>)}
            <option value="none">No role</option>
            <option value="techs-no-login">Techs without an account</option>
          </select>
          <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
            {rows.length} {rows.length === 1 ? "person" : "people"}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading} style={{ marginLeft: "auto" }}>Refresh</button>
        </div>

        {loading && !data ? (
          <div className="card" style={{ padding: 20 }}><div className="skeleton" style={{ height: 200, borderRadius: 8 }}/></div>
        ) : error ? (
          <div className="card" style={{ padding: "28px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Couldn't load users</div>
            <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", marginBottom: 12 }}>{error}</div>
            <button className="btn btn-secondary btn-sm" onClick={load}>Try again</button>
          </div>
        ) : (
          <div className="card">
            {/* .card clips overflow, so the table scrolls sideways in its own wrapper. */}
            <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th>Name</th><th>Account</th><th>Roles</th><th>Team</th>
                  <th title="Monthly report email, for technologists">Report email</th><th>USMON ID</th>
                  <th style={{ textAlign: "right" }}>Cases</th><th>Last case</th><th/>
                </tr>
              </thead>
              <tbody>
                {rows.map(u => {
                  const ls = loginState(u);
                  const rowBusy = busy === u.key;
                  const isMe = u.email.toLowerCase() === me;
                  const isEditing = editing === u.key;
                  return (
                    <tr key={u.key} style={{ opacity: rowBusy ? 0.5 : 1 }}>
                      <td>
                        <div style={{ fontSize: 12.5, fontWeight: 500 }}>{u.name || "—"}{isMe && <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}> (you)</span>}</div>
                        <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>{u.email}</div>
                      </td>
                      <td style={{ fontSize: 11.5, color: ls.color, whiteSpace: "nowrap" }}>{ls.label}</td>
                      <td>
                        {!isEditing ? (
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {u.roles.length === 0 && <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>—</span>}
                            {roles.filter(r => u.roles.includes(r)).map(r => {
                              const s = ROLE_STYLE[r] || ROLE_STYLE.Member;
                              return (
                                <span key={r} style={{ fontSize: 10, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 8px",
                                  borderRadius: 10, border: `1px solid ${s.border}`, background: s.bg, color: s.color }}>{s.label}</span>
                              );
                            })}
                          </div>
                        ) : (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {roles.map(r => {
                            const s = ROLE_STYLE[r] || ROLE_STYLE.Member;
                            if (!u.entraId) {
                              // No account yet: pick the roles the invite will give.
                              const picked = inviteRoles.includes(r);
                              const blocked = r === "SuperAdmin" || rowBusy;
                              return (
                                <button key={r} disabled={blocked} onClick={() => toggleInviteRole(r)}
                                  title={r === "SuperAdmin" ? "Super Admin can be added after they accept the invite" : picked ? "Won't be given on invite" : "Give on invite"}
                                  style={{ fontSize: 10, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                                    cursor: blocked ? "default" : "pointer",
                                    border: `1px dashed ${picked ? s.color : "var(--border)"}`,
                                    background: picked ? s.bg : "var(--white)", color: picked ? s.color : "var(--text-tertiary)",
                                    opacity: r === "SuperAdmin" ? 0.4 : 1 }}>
                                  {picked ? "✓ " : "+ "}{s.label}
                                </button>
                              );
                            }
                            const on = u.roles.includes(r);
                            const locked = rowBusy || (isMe && r === "SuperAdmin" && on);
                            return (
                              <button key={r} disabled={locked} onClick={() => toggleRole(u, r)}
                                title={isMe && r === "SuperAdmin" && on ? "You can't remove your own Super Admin role" : on ? "Click to remove" : "Click to add"}
                                style={{ fontSize: 10, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                                  cursor: locked ? "default" : "pointer",
                                  border: `1px solid ${on ? s.border : "var(--border)"}`,
                                  background: on ? s.bg : "var(--white)", color: on ? s.color : "var(--text-tertiary)" }}>
                                {on ? "✓ " : "+ "}{s.label}
                              </button>
                            );
                          })}
                          {!u.entraId && (
                            <div style={{ width: "100%", fontSize: 10.5, color: "var(--text-tertiary)", marginTop: 2 }}>
                              Roles to give when you send the invite
                            </div>
                          )}
                        </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {!u.techWebId ? (
                          <span style={{ color: "var(--text-tertiary)" }}>—</span>
                        ) : !isEditing ? (
                          u.team || <span style={{ color: "var(--text-tertiary)" }}>—</span>
                        ) : (
                          <select value={u.team || ""} disabled={rowBusy} onChange={e => setTeam(u, e.target.value)}
                            style={{ fontSize: 11.5, padding: "3px 6px", borderRadius: 5, border: "1px solid var(--border)", background: "var(--white)", maxWidth: 190 }}>
                            <option value="">—</option>
                            {(data?.teams || []).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {!u.techWebId ? (
                          <span style={{ color: "var(--text-tertiary)" }}>—</span>
                        ) : !isEditing ? (
                          <span style={{ color: u.monthlyEmail === false ? "var(--text-tertiary)" : "#0f766e" }}>
                            {u.monthlyEmail === false ? "Off" : "On"}
                          </span>
                        ) : (
                          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, cursor: "pointer" }}>
                            <input type="checkbox" checked={u.monthlyEmail !== false} disabled={rowBusy}
                              onChange={e => setMonthlyEmail(u, e.target.checked)}/>
                            {u.monthlyEmail === false ? "Off" : "On"}
                          </label>
                        )}
                      </td>
                      <td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>
                        {u.techWebId || <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>—</span>}
                      </td>
                      <td style={{ textAlign: "right", fontSize: 12 }}>{u.caseCount ?? "—"}</td>
                      <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{u.lastCase || "—"}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {isEditing && !u.entraId && u.email && (
                          <button className="btn btn-secondary btn-sm" disabled={rowBusy} onClick={() => invite(u)}
                            style={{ marginRight: 6 }}>
                            {inviteRoles.length
                              ? "Invite as " + roles.filter(r => inviteRoles.includes(r)).map(r => ROLE_STYLE[r]?.label || r).join(", ")
                              : "Invite (no role)"}
                          </button>
                        )}
                        {isEditing && u.userType === "Guest" && u.inviteState === "PendingAcceptance" && (
                          <button className="btn btn-secondary btn-sm" disabled={rowBusy} onClick={() => resend(u)}
                            style={{ marginRight: 6 }}>Resend invite</button>
                        )}
                        {isEditing && u.entraId && u.roles.length > 0 && !isMe && (
                          <button className="btn btn-secondary btn-sm" disabled={rowBusy} onClick={() => removeAccess(u)}
                            style={{ marginRight: 6, color: "#dc2626", borderColor: "#fecaca" }}>Remove access</button>
                        )}
                        {isEditing ? (
                          <button className="btn btn-primary btn-sm" disabled={rowBusy} onClick={() => setEditing(null)}>Done</button>
                        ) : (
                          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => startEdit(u)}>Edit</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 24, color: "var(--text-tertiary)" }}>No one matches.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
